/* Resources article — Stripe/Notion-grade reading experience matching the
   Legal Engine: sticky lateral ToC with scroll-spy, anchor deep-links,
   prev/next reading order, related guides. */
import { ArrowLeft, ArrowRight, ChevronDown, SearchX } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { ArticleBlocks } from "@/components/resources/ArticleBlocks";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useJsonLd, usePageMeta, PUBLIC_BASE } from "@/lib/seo";
import { BRAND } from "@/config/brand";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (window.history?.replaceState) window.history.replaceState(null, "", `#${id}`);
};

const Toc = ({ sections, activeId, onGo }) => {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("kb.onThisPage")} className="flex flex-col gap-1.5" data-testid="resource-toc">
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0A0A0A]/45 mb-1">{t("kb.onThisPage")}</span>
      {sections
        .filter((s) => s.title)
        .map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onGo(s.id)}
            data-testid={`resource-toc-${s.id}`}
            className={`text-left text-[13px] leading-snug py-0.5 pl-1 transition-colors duration-200 ${
              activeId === s.id ? "text-[#1D4D3B] font-medium" : "text-[#0A0A0A]/60 hover:text-[#0A0A0A]"
            }`}
          >
            {s.title}
          </button>
        ))}
    </nav>
  );
};

const PagerCard = ({ article, dir }) => {
  const { t } = useTranslation();
  if (!article) return <span aria-hidden="true" />;
  return (
    <Link
      to={`/resources/${article.slug}`}
      data-testid={`resource-${dir}`}
      className={`group flex flex-col gap-1 rounded-2xl border border-black/5 bg-white p-5 hover:border-black/15 transition-colors duration-200 ${
        dir === "next" ? "items-end text-right" : ""
      }`}
    >
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0A0A0A]/40">
        {dir === "prev" && <ArrowLeft className="w-3 h-3" aria-hidden="true" />}
        {t(dir === "prev" ? "kb.previous" : "kb.next")}
        {dir === "next" && <ArrowRight className="w-3 h-3" aria-hidden="true" />}
      </span>
      <span className="text-sm font-semibold text-[#0A0A0A] group-hover:text-[#1D4D3B] transition-colors duration-200">
        {article.title}
      </span>
    </Link>
  );
};

export default function ResourceArticlePage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [article, setArticle] = useState(undefined);
  const [activeId, setActiveId] = useState(null);
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    setArticle(undefined);
    setTocOpen(false);
    api
      .get(`/resources/articles/${slug}`, { params: { locale: i18n.resolvedLanguage } })
      .then(({ data }) => setArticle(data))
      .catch(() => setArticle(null));
  }, [slug, i18n.resolvedLanguage]);

  usePageMeta({
    title: article ? `${article.title} — ${BRAND.name}` : undefined,
    description: article?.subtitle,
    localized: true,
    ogType: "article",
  });
  /* WS13/WS7: Article + BreadcrumbList — entidad editora real, fecha real. */
  useJsonLd(
    article
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: article.title,
              description: article.subtitle || undefined,
              dateModified: article.updated || undefined,
              inLanguage: i18n.resolvedLanguage,
              mainEntityOfPage: `${PUBLIC_BASE}/resources/${article.slug}`,
              publisher: { "@type": "Organization", name: BRAND.name, url: PUBLIC_BASE },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: BRAND.name, item: PUBLIC_BASE },
                { "@type": "ListItem", position: 2, name: t("kb.title"), item: `${PUBLIC_BASE}/resources` },
                { "@type": "ListItem", position: 3, name: article.title, item: `${PUBLIC_BASE}/resources/${article.slug}` },
              ],
            },
          ],
        }
      : null
  );

  const sectionIds = useMemo(() => (article ? article.sections.map((s) => s.id) : []), [article]);

  useEffect(() => {
    if (!article) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-10% 0px -75% 0px" }
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    if (window.location.hash) {
      setTimeout(() => scrollTo(window.location.hash.slice(1)), 150);
    }
    return () => observer.disconnect();
  }, [article, sectionIds]);

  if (article === undefined) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 flex flex-col gap-8" data-testid="resource-article-skeleton">
        <Skeleton className="h-4 w-32 rounded-lg" />
        <Skeleton className="h-12 w-2/3 rounded-xl" />
        <div className="grid lg:grid-cols-[260px_1fr] gap-14">
          <Skeleton className="hidden lg:block h-64 rounded-2xl" />
          <div className="flex flex-col gap-4">
            <Skeleton className="h-24 rounded-2xl" />
            <Skeleton className="h-24 rounded-2xl" />
            <Skeleton className="h-24 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (article === null) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 flex flex-col items-center gap-6">
        <EmptyState icon={SearchX} testid="resource-not-found" title={t("kb.notFoundTitle")} description={t("kb.notFoundDesc")} />
        <Link
          to="/resources"
          className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-black/80 transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          {t("kb.backToResources")}
        </Link>
      </div>
    );
  }

  const words = article.sections.reduce((n, s) => n + s.blocks.reduce((m, b) => m + b.text.split(/\s+/).length, 0), 0);
  const minutes = Math.max(1, Math.round(words / 220));
  const updated = article.updated
    ? new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: "long" }).format(new Date(article.updated))
    : null;
  const audienceKey = ["buyer", "organizer"].includes(article.audience) ? article.audience : "all";

  const go = (id) => {
    setTocOpen(false);
    scrollTo(id);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 pb-24" data-testid="resource-article-page">
      <header className="pt-14 pb-8 border-b border-black/5 mb-6">
        <Link
          to="/resources"
          data-testid="resource-back"
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#0A0A0A]/50 hover:text-[#0A0A0A] transition-colors duration-200 mb-5"
        >
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
          {t("kb.backToResources")}
        </Link>
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#1D4D3B] mb-3">{t(`kb.cat.${article.category}`)}</p>
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-[#0A0A0A]" data-testid="resource-article-title">
          {article.title}
        </h1>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="inline-flex items-center rounded-full bg-black/5 px-3 py-1 text-xs font-medium text-[#0A0A0A]/70">
            {t(`kb.audience.${audienceKey}`)}
          </span>
          <span className="inline-flex items-center rounded-full bg-black/5 px-3 py-1 text-xs font-medium text-[#0A0A0A]/70">
            {t("kb.minRead", { count: minutes })}
          </span>
          {updated && <span className="text-xs text-[#737373]">{t("kb.updated", { date: updated })}</span>}
        </div>
      </header>

      {/* mobile ToC */}
      <div className="lg:hidden mb-6">
        <button
          type="button"
          data-testid="resource-toc-toggle"
          onClick={() => setTocOpen((v) => !v)}
          className="w-full flex items-center justify-between rounded-2xl border border-black/10 px-5 py-3.5 text-sm font-medium text-[#0A0A0A]"
        >
          {t("kb.onThisPage")}
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${tocOpen ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        {tocOpen && (
          <div className="mt-3 rounded-2xl border border-black/5 bg-[#FAFAF9] p-5 max-h-80 overflow-y-auto">
            <Toc sections={article.sections} activeId={activeId} onGo={go} />
          </div>
        )}
      </div>

      <div className="lg:grid lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-14">
        <aside className="hidden lg:block">
          <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2 pb-8">
            <Toc sections={article.sections} activeId={activeId} onGo={go} />
          </div>
        </aside>
        <div className="min-w-0">
          <article>
            {article.sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-28 mb-9">
                {s.title && <h2 className="text-lg font-semibold tracking-tight text-[#0A0A0A] mb-1">{s.title}</h2>}
                <ArticleBlocks blocks={s.blocks} />
              </section>
            ))}
          </article>

          {(article.prev || article.next) && (
            <nav className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12 pt-8 border-t border-black/5" aria-label={`${t("kb.previous")} / ${t("kb.next")}`}>
              <PagerCard article={article.prev} dir="prev" />
              <PagerCard article={article.next} dir="next" />
            </nav>
          )}

          {article.related_articles?.length > 0 && (
            <section className="mt-12 flex flex-col gap-4">
              <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-[#0A0A0A]/45">{t("kb.related")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {article.related_articles.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/resources/${r.slug}`}
                    data-testid={`resource-related-${r.slug}`}
                    className="group flex flex-col gap-1.5 rounded-2xl border border-black/5 bg-white p-5 hover:border-black/15 transition-colors duration-200"
                  >
                    <span className="text-sm font-semibold text-[#0A0A0A] group-hover:text-[#1D4D3B] transition-colors duration-200">{r.title}</span>
                    <span className="text-xs text-[#737373] leading-relaxed line-clamp-2">{r.subtitle}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
